import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CybersocxdrService } from '../../shared/services/xtendedsoc/cybersocxdr.service';
import { TranslateService } from '@ngx-translate/core';
import { CaseXDR } from 'src/app/shared/model/cybersocxdr/microsocticket';
import { constants } from '../../shared/constants';
import { CybersocXDRDashboardSearchCriteria } from '../../shared/model/searchCriteria';
import { MenuItem } from '../components/filtermenu/filtermenu.component';
import { CybersocxdrglobalfiltersService } from '../cybersocxdrglobalfilters.service';
import FileSaver from 'file-saver';
import { AlertService } from 'src/app/shared/services/alert.service';

@Component({
  selector: 'app-cybersocxdrincidents',
  templateUrl: './cybersocxdrincidents.component.html',
  styleUrls: ['./cybersocxdrincidents.component.scss'],
})
export class CybersocxdrIncidentsComponent implements OnInit {
  @Output() searchEmitter = new EventEmitter<CybersocXDRDashboardSearchCriteria>(true);

  public scrollLoading = false;

  currentPage: number;
  page: string;

  // data
  searched: boolean;
  cases: CaseXDR[];
  casesAll: CaseXDR[];
  casesCount: number;
  casesLoaded: boolean;
  createdFrom: string;
  createdTo: string;
  casesCountPrettier: string;
  groupedIncidents: CaseXDR[];
  limit: number;
  caseNumber: string;
  //filters
  groupBy: string;

  flags: any[] = [];

  sortedBy: string;
  sortedType: string;

  lockedLoading: boolean;

  incidentsFlagged: CaseXDR[];

  filterMenuItems: MenuItem[] = [
    {
      text: 'Filter In',
      icon: 'plus-circle',
      handler: (point) => {
        this.globalFilters.addFilter(point, true);
        this.search();
      },
    },
    {
      text: 'Filter Out',
      icon: 'minus-circle',
      handler: (point) => {
        this.globalFilters.addFilter(point, false);
        this.search();
      },
    },
  ];

  constructor(
    private cybersocxdrService: CybersocxdrService,
    private translate: TranslateService,
    private globalFilters: CybersocxdrglobalfiltersService,
    private alertService: AlertService
  ) {}

  public loadNextCases() {
    if (
      !this.cases ||
      this.cases.length >= this.casesCount ||
      this.lockedLoading ||
      this.page !== 'cases' ||
      this.scrollLoading
    ) {
      return;
    }

    this.scrollLoading = true;
    this.globalFilters.searchCriteria.skip = this.cases.length;
    this.cybersocxdrService.getCases(this.globalFilters.searchCriteria).then((cases: CaseXDR[]) => {
      this.cases = this.cases.concat(cases);
      this.scrollLoading = false;
    });
  }

  async search() {
    const searchCriteria = this.globalFilters.searchCriteria;
    this.createdTo = searchCriteria.createdTo;
    this.createdFrom = searchCriteria.createdFrom;
    this.searched = true;
    searchCriteria.limit = this.limit;
    searchCriteria.skip = 0;

    if (this.sortedBy) {
      searchCriteria.sortBy = {
        field: this.sortedBy,
        type: this.sortedType,
      };
    }

    [this.groupedIncidents, this.cases, this.casesCount] = await Promise.all([
      this.updateGroupBy('severities'),
      this.cybersocxdrService.getCases(searchCriteria),
      (await this.cybersocxdrService.countCases(searchCriteria)).incidentCount,
    ]);
    this.casesAll = this.cases;
    this.casesCountPrettier = Number(this.casesCount).toLocaleString();
    this.casesLoaded = true;
    this.lockedLoading = false;
  }

  async exportCases() {
    const searchCriteria = this.globalFilters.searchCriteria;

    if (this.sortedBy) {
      searchCriteria.sortBy = {
        field: this.sortedBy,
        type: this.sortedType,
      };
    }

    (await this.cybersocxdrService.casesCSVExport(searchCriteria)).subscribe({
      next: (val: any) => {
        const file = new Blob([val], { type: 'text/csv' });
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
          (window.navigator as any).msSaveOrOpenBlob(file);
        } else {
          FileSaver.saveAs(file, 'incidents.csv');
        }
      },
      error: async (err) => {
        const message = JSON.parse(await err.error.text()).message;
        this.alertService.handlerError(message);
      },
    });
  }

  async searchCase(event) {
    const { type, value } = event;
    if (type === 'caseNumber') {
      const singleCase = await this.cybersocxdrService.getCase(parseInt(value, 10));
      this.cases = [];
      if (singleCase) {
        this.cases = [singleCase];
      }
      return;
    }
    if (type === 'subject') {
      this.cases = await this.cybersocxdrService.getCasesBySubject(value);
      this.casesCountPrettier = Number(this.cases.length).toLocaleString();
      this.lockedLoading = true;
    }
  }

  async ngOnInit() {
    this.lockedLoading = false;
    this.currentPage = 1;
    this.page = 'cases';
    this.casesLoaded = true;
    this.casesCount = 0;
    this.limit = constants.recordsPerPage.microsocticket;
    this.flags = await this.cybersocxdrService.flagCase('-1');
    this.incidentsFlagged = await this.cybersocxdrService.getFlaggedCases(this.flags);
  }

  async updateGroupBy(groupBy: string): Promise<CaseXDR[]> {
    this.groupedIncidents = await this.cybersocxdrService.getCasesGroupBy(this.globalFilters.searchCriteria, groupBy);
    return this.groupedIncidents;
  }

  loadCases() {
    this.casesLoaded = false;
    switch (this.page) {
      case 'cases':
        this.cases = this.casesAll;
        break;
      case 'flagged':
        this.cases = this.incidentsFlagged;
        break;
      default:
        this.cases = this.casesAll;
        break;
    }
    this.casesLoaded = true;
  }

  async updateCaseSearch(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<void> {
    this.casesLoaded = false;
    searchCriteria.limit = this.limit;
    this.cases = await this.cybersocxdrService.getCases(searchCriteria);
    this.casesCount = (await this.cybersocxdrService.countCases(searchCriteria)).incidentCount;
    this.casesLoaded = true;
  }

  async onFlag(caseRef: string) {
    this.flags = await this.cybersocxdrService.flagCase(caseRef);
    this.incidentsFlagged = await this.cybersocxdrService.getFlaggedCases(this.flags);
    this.loadCases();
  }

  async sortCases(sortBy: string) {
    this.casesLoaded = false;
    this.sortedBy = sortBy;
    this.sortedType = this.sortedType === 'asc' ? 'desc' : 'asc';
    this.search();
    this.casesLoaded = true;
  }
}
